import React, { useState, useEffect } from 'react';
import { FiX, FiPlus, FiTrash2 } from 'react-icons/fi';

export default function TourCompanyForm({ isOpen, onClose, onSubmit, initialData }) {
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    contact: { person: '', phone: '', email: '', address: '' },
    service_types: [],
    service_areas: [],
    pricing: { base_price: 0, commission_type: 'percentage', commission_value: 10, payment_terms: '' },
    status: 'pending',
    notes: '',
  });
  const [loading, setLoading] = useState(false);

  const serviceTypeOptions = [
    { value: 'safari', label: 'Safari' },
    { value: 'diving', label: 'Diving' },
    { value: 'horse', label: 'Horse Riding' },
    { value: 'boat', label: 'Boat Trips' },
    { value: 'cultural', label: 'Cultural Tours' },
    { value: 'luxury', label: 'Luxury' },
  ];

  const serviceAreas = [
    'hurghada', 'marsa_alam', 'luxor', 'aswan', 'cairo', 'sharm_el_sheikh'
  ];

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        slug: initialData.slug || '',
        contact: initialData.contact || { person: '', phone: '', email: '', address: '' },
        service_types: initialData.service_types || [],
        service_areas: initialData.service_areas || [],
        pricing: initialData.pricing || { base_price: 0, commission_type: 'percentage', commission_value: 10, payment_terms: '' },
        status: initialData.status || 'pending',
        notes: initialData.notes || '',
      });
    }
  }, [initialData]);

  if (!isOpen) return null;

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'name') {
      setFormData({ ...formData, name: value, slug: generateSlug(value) });
    } else if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value,
        },
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleServiceTypeToggle = (type) => {
    setFormData({
      ...formData,
      service_types: formData.service_types.includes(type)
        ? formData.service_types.filter(t => t !== type)
        : [...formData.service_types, type],
    });
  };

  const handleAreaToggle = (area) => {
    setFormData({
      ...formData,
      service_areas: formData.service_areas.includes(area)
        ? formData.service_areas.filter(a => a !== area)
        : [...formData.service_areas, area],
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await onSubmit(formData);
    setLoading(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white">
          <h2 className="text-xl font-semibold">{initialData ? 'Edit Tour Company' : 'Add New Tour Company'}</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <FiX className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
              <input
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
              />
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-md font-medium text-gray-800 mb-3">Contact Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Contact Person</label>
                <input
                  type="text"
                  name="contact.person"
                  value={formData.contact.person}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Phone *</label>
                <input
                  type="tel"
                  name="contact.phone"
                  value={formData.contact.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Email</label>
                <input
                  type="email"
                  name="contact.email"
                  value={formData.contact.email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Address</label>
                <input
                  type="text"
                  name="contact.address"
                  value={formData.contact.address}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
          </div>

          {/* Service Types */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Service Types</label>
            <div className="grid grid-cols-3 gap-2">
              {serviceTypeOptions.map(type => (
                <label key={type.value} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.service_types.includes(type.value)}
                    onChange={() => handleServiceTypeToggle(type.value)}
                    className="mr-2"
                  />
                  <span className="text-sm">{type.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Service Areas */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Service Areas</label>
            <div className="grid grid-cols-3 gap-2">
              {serviceAreas.map(area => (
                <label key={area} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.service_areas.includes(area)}
                    onChange={() => handleAreaToggle(area)}
                    className="mr-2"
                  />
                  <span className="text-sm capitalize">{area.replace('_', ' ')}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Pricing */}
          <div>
            <h3 className="text-md font-medium text-gray-800 mb-3">Pricing & Commission</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Base Price ($)</label>
                <input
                  type="number"
                  name="pricing.base_price"
                  value={formData.pricing.base_price}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Commission Type</label>
                <select
                  name="pricing.commission_type"
                  value={formData.pricing.commission_type}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="fixed">Fixed Amount</option>
                  <option value="percentage">Percentage (%)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Commission Value</label>
                <input
                  type="number"
                  name="pricing.commission_value"
                  value={formData.pricing.commission_value}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="col-span-3">
                <label className="block text-sm text-gray-600 mb-1">Payment Terms</label>
                <input
                  type="text"
                  name="pricing.payment_terms"
                  value={formData.pricing.payment_terms}
                  onChange={handleChange}
                  placeholder="e.g., Net 15, Net 30, Weekly"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
          </div>

          {/* Status & Notes */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="pending">Pending</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <input
                type="text"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 transition"
            >
              {loading ? 'Saving...' : initialData ? 'Update Company' : 'Create Company'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
